import { CSS_SCOPE_AT_RULE, CSS_STRING_SPECIALS_RE } from "./constants";
import { isSafeCustomCss } from "./customCss";

type ScopedCssOptions = Readonly<{
  /**
   * Where the `<style>` goes: the document's head, or the shadow root the
   * surface renders in (a document stylesheet never reaches into a shadow tree).
   */
  root: Document | ShadowRoot;
  /** The attribute every element the CSS may style carries, e.g. `data-redacto-notice-id`. */
  scopeAttribute: string;
  /** That attribute's value for this one surface. */
  scopeId: string;
  /** Set on the injected `<style>`, valued with `scopeId`, so it can be found again. */
  markerAttribute: string;
  css: string;
}>;

/**
 * Whether the browser parsed anything outside the `@scope` block. The token
 * guard already refuses unbalanced CSS; this reads what the browser actually
 * built, so a tokenizer quirk the guard misses (a bad `url(` token hiding a
 * `}`) still cannot reach the host page. A browser without `@scope` drops the
 * block whole, which leaves only escaped rules behind. With no sheet to read
 * (a DOM without CSS parsing) there is nothing to check.
 */
const escapesScope = (style: HTMLStyleElement): boolean =>
  Array.from(style.sheet?.cssRules ?? []).some(
    (rule) => !rule.cssText.startsWith(CSS_SCOPE_AT_RULE),
  );

/**
 * Injects an admin's custom CSS scoped to one surface: `@scope` keeps every
 * rule inside the elements carrying `scopeAttribute="scopeId"`, so nothing
 * reaches the host page. CSS that fails the token guard is never injected,
 * and injected CSS is removed again if the browser parsed any of it outside
 * the scope. Returns the cleanup that removes it.
 */
export const applyScopedCustomCss = ({
  root,
  scopeAttribute,
  scopeId,
  markerAttribute,
  css,
}: ScopedCssOptions): (() => void) => {
  if (typeof document === "undefined" || !css.trim() || !isSafeCustomCss(css)) {
    return () => undefined;
  }
  const id = scopeId.replace(CSS_STRING_SPECIALS_RE, "\\$&");
  const style = document.createElement("style");
  style.setAttribute(markerAttribute, scopeId);
  style.textContent = `${CSS_SCOPE_AT_RULE} ([${scopeAttribute}="${id}"]) {\n${css}\n}`;
  (root instanceof Document ? root.head : root).appendChild(style);
  if (escapesScope(style)) {
    style.remove();
    return () => undefined;
  }
  return () => style.remove();
};
